import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, MapPin, CheckCircle2, Building2, ExternalLink, FileText, Lock, Users, Phone, Mail } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const licensedStates = [
  { state: 'Texas (Primary HQ)', status: 'Active & Licensed', code: 'TX' },
  { state: 'California', status: 'Active & Licensed', code: 'CA' },
  { state: 'Colorado', status: 'Active & Licensed', code: 'CO' },
  { state: 'Florida', status: 'Active & Licensed', code: 'FL' },
  { state: 'Idaho', status: 'Active & Licensed', code: 'ID' },
  { state: 'Illinois', status: 'Active & Licensed', code: 'IL' },
  { state: 'Kansas', status: 'Active & Licensed', code: 'KS' },
  { state: 'Ohio', status: 'Active & Licensed', code: 'OH' },
  { state: 'South Carolina', status: 'Active & Licensed', code: 'SC' }
];

const carriers = [
  { name: 'Allianz Life', category: 'Fixed Index Annuities & Life' },
  { name: 'American Amicable Group', category: 'Simplified Issue & Term Life' },
  { name: 'American Equity', category: 'Fixed Index Annuities' },
  { name: 'Americo Annuities & Life', category: 'Annuities & Family Protection' },
  { name: 'Ameritas', category: 'Life & Wealth Accumulation' },
  { name: 'Annexus Annuity & Life', category: 'Index Annuities & Accumulation' },
  { name: 'Athene Annuities', category: 'Retirement Index Annuities' },
  { name: 'Corebridge Financial', category: 'Life Insurance & Annuities (TMG)' },
  { name: 'Ethos', category: 'Digital Term & Life Solutions' },
  { name: 'F&G Life', category: 'Annuities & Life Insurance' },
  { name: 'Lincoln Financial', category: 'Wealth Preservation & Annuities' },
  { name: 'Mutual of Omaha', category: 'Living Benefits & Protection' },
  { name: 'Nationwide Life', category: 'Index Life & Retirement Solutions' },
  { name: 'North American Life', category: 'Sammons Financial Group' }
];

export default function Licensing() {
  return (
    <div className="bg-[#070E1B] text-white min-h-screen font-opensans flex flex-col justify-between">
      <SEOHead 
        title="State Licensing Credentials & Carrier Network | Grow & Stay Rich"
        description="Verify state licensing details, NPN 21450620, SureLC portal verification, HGI / Integrity Marketing Group agency affiliation, and top-tier insurance carrier partners."
        path="/licensing"
      />

      <div>
        <Navigation />

        {/* ── HERO BANNER ───────────────── */}
        <div className="relative pt-24 pb-16 border-b border-[#C9A84C]/25 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-[#C9A84C]/15 rounded-full blur-[150px] animate-pulse-glow" />
            <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] bg-[#3D6ACC]/20 rounded-full blur-[160px]" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
              <Shield className="w-4 h-4 text-[#C9A84C]" />
              Regulatory Transparency & Credentials
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-playfair tracking-tight leading-tight">
              State Licensing & <span className="text-gradient-gold">Agency Credentials</span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl font-opensans">
              Grow & Stay Rich operates under strict state insurance regulatory compliance. Contracting and licensing credentials are verified through <strong>SureLC</strong> and backed by agency leadership under <strong>Hegemon Group International (HGI)</strong>, a member agency of <strong>Integrity Marketing Group</strong>.
            </p>

            {/* Key Licensing Badge Card Grid */}
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-5 backdrop-blur-xl space-y-1">
                <div className="text-xs text-slate-400 font-semibold uppercase">National Producer Number</div>
                <div className="text-xl font-bold text-gradient-gold font-playfair">NPN: 21450620</div>
                <div className="text-[11px] text-slate-300">National Insurance Producer Registry (NIPR) Verified</div>
              </div>

              <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-5 backdrop-blur-xl space-y-1">
                <div className="text-xs text-slate-400 font-semibold uppercase">Primary Domicile State</div>
                <div className="text-xl font-bold text-white font-playfair">Texas (TX)</div>
                <div className="text-[11px] text-slate-300">Resident Life, Health & Fixed Annuity License</div>
              </div>

              <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-5 backdrop-blur-xl space-y-1">
                <div className="text-xs text-slate-400 font-semibold uppercase">Agency Network</div>
                <div className="text-xl font-bold text-[#E8D4A0] font-playfair">HGI / Integrity</div>
                <div className="text-[11px] text-slate-300">Hegemon Group International Marketing Network</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ───────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
          
          {/* 1. STATE LICENSING ROSTER GRID */}
          <section className="space-y-6">
            <div className="border-b border-slate-800 pb-4">
              <h2 className="text-2xl sm:text-3xl font-bold font-playfair text-[#E8D4A0] flex items-center gap-2.5">
                <MapPin className="w-6 h-6 text-[#C9A84C]" />
                Active Licensed States Roster
              </h2>
              <p className="text-slate-300 text-sm mt-1">
                We are authorized to conduct life insurance, annuity, and financial advisory consulting across the following states:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {licensedStates.map((st, idx) => (
                <div key={idx} className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 rounded-2xl p-4 flex items-center justify-between backdrop-blur-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-[#162236] border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] font-bold text-xs">
                      {st.code}
                    </div>
                    <div>
                      <div className="font-bold text-sm text-white">{st.state}</div>
                      <div className="text-[11px] text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>{st.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 2. CARRIER PARTNER LOGO & PROVIDER GRID */}
          <section className="space-y-6">
            <div className="border-b border-slate-800 pb-4">
              <h2 className="text-2xl sm:text-3xl font-bold font-playfair text-[#E8D4A0] flex items-center gap-2.5">
                <Building2 className="w-6 h-6 text-[#C9A84C]" />
                Top-Tier Carrier Representation
              </h2>
              <p className="text-slate-300 text-sm mt-1">
                As an independent advisory network, we partner with premier A-rated financial and insurance carriers across the United States:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {carriers.map((c, idx) => (
                <div key={idx} className="bg-[#162236]/90 border border-[#C9A84C]/25 rounded-2xl p-5 backdrop-blur-xl flex items-center gap-4 hover:border-[#C9A84C]/60 transition-all">
                  <div className="p-3 bg-[#0D1B2A] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C] shrink-0 font-bold text-xs">
                    0{idx + 1}
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-white font-playfair">{c.name}</h3>
                    <p className="text-xs text-slate-400">{c.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 3. CORPORATE COMPLIANCE & ADDRESS DISCLOSURE */}
          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-4">
            <h3 className="text-xl font-bold font-playfair text-white flex items-center gap-2.5">
              <Lock className="w-5 h-5 text-[#C9A84C]" />
              Regulatory Address & Verification
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-opensans">
              For regulatory communications, state department compliance inquiries, and official verification:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 pt-2 text-xs text-slate-300 font-mono bg-[#162236] p-4 rounded-xl border border-[#C9A84C]/20">
              <div>
                <strong>HQ Regulatory Address:</strong><br />
                2019 Holy Unity Ln, St Paul, TX-75098
              </div>
              <div>
                <strong>Official Direct Line:</strong> (903) 231-3051<br />
                <strong>Support Contact:</strong> info@growandstayrich.com
              </div>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
}
